import { invalidateAllCompanyFilterQueries } from '@Queries/index';
import { ESelectionState, ISystemJob } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useCompaniesPageUrlState } from '..';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useModal } from '../../../../../models/hooks/useModal';
import { useDeleteCompaniesMutation } from '../../../../../queries/Company';
import { useExportCompaniesMutation } from '../../../../../queries/Company/useExportCompaniesMutation';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../../components-v2/Dropdown';
import { MergeCompaniesWizard } from '../../../../components/companies/MergeCompaniesWizard';
import { DeleteConfirmation } from '../../../../components/DeleteConfirmation';
import { ExportConfirmationModal } from '../../../../components/ExportConfirmation';
import { Modal } from '../../../../components/Modal';
import { ExportIcon } from '../../../../components/svgs/icons/ExportIcon';
import { MergeIcon } from '../../../../components/svgs/icons/MergeIcon';
import { TrashIcon } from '../../../../components/svgs/icons/TrashIcon';
import { header } from '../../../../styles/colors';
import { bs } from '../../../../styles/styles';
import { useCompaniesSearch } from '../CompaniesContext';
import { styleSheet } from './styles';

const useActionBarStatus = () => {
	const { selectionState, selectedCompanyIds } = useCompaniesSearch();
	const isSelectingAll =
		selectionState === ESelectionState.All ||
		(selectionState === ESelectionState.Some && selectedCompanyIds.length === 0);

	const allOrSomeAreSelected = selectedCompanyIds.length > 0;
	const canDelete = allOrSomeAreSelected;
	const canMerge = selectedCompanyIds.length > 1;
	const canExport = allOrSomeAreSelected || isSelectingAll;

	return { canDelete, canExport, canMerge } as const;
};

export const CompaniesActionsBar: React.FC = observer(() => {
	const {
		companies,
		selectedCompanyIds,
		setSelectedCompanyIds,
		selectionState,
		setSelectionState,
		excludedCompanyIds,
		setExcludedCompanyIds,
	} = useCompaniesSearch();
	const errorMessages = useErrorMessages();
	const { canMerge, canDelete, canExport } = useActionBarStatus();
	const userSession = useUserSession();

	const [exportCompaniesPromise, setExportCompaniesPromise] = React.useState<Promise<ISystemJob>>(null);
	const [{ companySearchProperty, companySearchValue }] = useCompaniesPageUrlState();

	const deleteCompaniesMutation = useDeleteCompaniesMutation({
		impersonationContext: userSession.impersonationContext,
		onSuccess: () => {
			const count = selectedCompanyIds.length;
			toast.success(`${count} compan${count > 1 ? 'ies' : 'y'} successfully deleted`);
			setSelectedCompanyIds([]);
			setSelectionState(ESelectionState.None);
		},
	});

	const exportCompaniesMutation = useExportCompaniesMutation({
		onSuccess: () => {
			toast.success('Export initiated. You will receive an email when the export is ready.');
			setSelectedCompanyIds([]);
			setSelectionState(ESelectionState.None);
			setExcludedCompanyIds([]);
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const mergeModal = useModal(false, () => {
		// noop
	}, [selectedCompanyIds, setSelectedCompanyIds]);

	const deleteModal = useModal(
		false,

		async (shouldDelete: boolean) => {
			if (!shouldDelete) {
				return;
			}
			await deleteCompaniesMutation.mutateAsync(selectedCompanyIds);
			invalidateAllCompanyFilterQueries();
		},
		[selectedCompanyIds, setSelectedCompanyIds]
	);

	const onDeleteClicked = React.useCallback(() => {
		deleteModal.setIsOpen(true)();
	}, [deleteModal]);

	const onExportCompaniesButtonClicked = () => {
		const sendAllSelectedToApi =
			selectionState === ESelectionState.All ||
			(selectionState === ESelectionState.Some && excludedCompanyIds.length > 0);

		const promise = exportCompaniesMutation.mutateAsync({
			includeContacts: false,
			includeCompanyIds: sendAllSelectedToApi ? null : selectedCompanyIds,
			deprecatedFilter: sendAllSelectedToApi
				? { searches: [{ property: companySearchProperty, value: companySearchValue }] }
				: null,
			excludeCompanyIds: excludedCompanyIds,
		});
		setExportCompaniesPromise(promise);
	};

	const onExportCompaniesAndContactsButtonClicked = () => {
		const sendAllSelectedToApi =
			selectionState === ESelectionState.All ||
			(selectionState === ESelectionState.Some && excludedCompanyIds.length > 0);

		setExportCompaniesPromise(
			exportCompaniesMutation.mutateAsync({
				includeContacts: true,
				includeCompanyIds: sendAllSelectedToApi ? null : selectedCompanyIds,
				deprecatedFilter: sendAllSelectedToApi
					? { searches: [{ property: companySearchProperty, value: companySearchValue }] }
					: null,
				excludeCompanyIds: excludedCompanyIds,
			})
		);
	};

	const onExportCompaniesClose = () => {
		setExportCompaniesPromise(null);
	};

	return (
		<div className={css(styleSheet.container)}>
			<button
				className={css(styleSheet.button, styleSheet.merge, !canMerge && styleSheet.disabled)}
				onClick={mergeModal.setIsOpen(true)}
			>
				<span>
					<MergeIcon />
					<span className={css(styleSheet.buttonText)}>Merge</span>
				</span>
			</button>
			<button
				className={css(styleSheet.button, styleSheet.delete, !canDelete && styleSheet.disabled)}
				onClick={onDeleteClicked}
			>
				<span>
					<TrashIcon fillColor={header} />
					<span className={css(styleSheet.buttonText)}>Delete</span>
				</span>
			</button>
			<Dropdown>
				<DropdownTrigger
					render={
						<button className={css(styleSheet.button, styleSheet.export, !canExport && styleSheet.disabled)}>
							<span>
								<ExportIcon />
								<span className={css(styleSheet.buttonText)}>Export</span>
							</span>
						</button>
					}
				/>
				<DropdownContent styles={[bs.w64]}>
					<DropdownItem onClick={onExportCompaniesButtonClicked}>Export only companies</DropdownItem>
					<DropdownItem onClick={onExportCompaniesAndContactsButtonClicked}>
						Export both companies & all contacts
					</DropdownItem>
				</DropdownContent>
			</Dropdown>
			<ExportConfirmationModal
				promise={exportCompaniesPromise}
				modalProps={{
					isOpen: !!exportCompaniesPromise,
					onRequestClose: onExportCompaniesClose,
				}}
				subTitle={
					<>
						<p>The fields we&apos;ll export include: Company Name, Company, Job Title,</p>
						<p>Address, Phone Numbers, Email Address, OwnerID, and tags.</p>
					</>
				}
			/>
			<Modal isOpen={mergeModal.isOpen} onRequestClose={mergeModal.onRequestClose}>
				<MergeCompaniesWizard
					selectedCompanies={companies.filter(x => selectedCompanyIds.includes(x.id))}
					onClose={mergeModal.onRequestClose}
				/>
			</Modal>
			<DeleteConfirmation
				bodyText='Are you sure you want to delete these companies?'
				isOpen={deleteModal.isOpen}
				onFinish={deleteModal.onRequestClose}
			/>
		</div>
	);
});
